import React, { useState, useEffect } from "react";
import axios from "axios";
import API_URL from "../config";
import "./TransfertArgent.css";

export default function TransfertArgent({ entrepriseId, siteId, gestionnaireId }) {
  const [form, setForm] = useState({ montant: "", date: "", motif: "" });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [montantDisponible, setMontantDisponible] = useState(null);

  const [encaissements, setEncaissements] = useState([]);
  const [encaisLoading, setEncaisLoading] = useState(false);
  const [encaisError, setEncaisError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ montant: "", date_encaissement: "", motif: "" });
  const [editLoading, setEditLoading] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false); 
  const [showHistory, setShowHistory] = useState(false); 

  useEffect(() => {
    fetchMontantDisponible();
  }, [entrepriseId, siteId]);

  useEffect(() => {
    if (showHistory) {
      fetchEncaissements();
    }
  }, [showHistory, entrepriseId, siteId]);

  const fetchMontantDisponible = async () => {
    if (!entrepriseId || !siteId) return;
    try {
      const res = await axios.get(`${API_URL}/api/transfert/${entrepriseId}/${siteId}`);
      setMontantDisponible(res.data.montantDisponible ?? null);
    } catch (err) {
      console.error("❌ Erreur récupération montant disponible :", err);
      setMontantDisponible(null);
    }
  };

  const fetchEncaissements = async () => {
    if (!entrepriseId || !siteId) return;
    try {
      setEncaisLoading(true);
      setEncaisError(null);
      const res = await axios.get(`${API_URL}/api/transfert/encaissement?entrepriseId=${entrepriseId}&siteId=${siteId}`);
      setEncaissements(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Erreur récupération encaissements :", err);
      setEncaisError("Impossible de récupérer l'historique.");
      setEncaissements([]);
    } finally {
      setEncaisLoading(false);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.montant || !form.date) {
      setMessage({ type: "error", text: "⚠️ Veuillez remplir tous les champs obligatoires." });
      return;
    }
    setLoading(true); 
    setMessage(null);
    try {
      const res = await axios.post(`${API_URL}/api/transfert`, {
        entrepriseId, siteId, gestionnaireId,
        montant: parseFloat(form.montant),
        date: form.date,
        motif: form.motif,
      });

      if (res.data.ok) {
        setMessage({ type: "success", text: "✅ Transfert enregistré avec succès !" });
        setForm({ montant: "", date: "", motif: "" });
        await fetchMontantDisponible();
      } else {
        setMessage({ type: "error", text: res.data.message || "Erreur lors du transfert." });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: err.response?.data?.message || "Erreur serveur." });
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirmer la suppression ?")) return;
    try {
      await axios.delete(`${API_URL}/api/transfert/encaissement/${id}`);
      setMessage({ type: "success", text: "✅ Encaissement supprimé." });
      await fetchMontantDisponible();
      await fetchEncaissements();
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Erreur suppression." });
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      montant: row.montant ?? "",
      date_encaissement: (row.date_encaissement || row.date || "").slice(0, 10),
      motif: row.motif ?? "",
    });
  };

  const cancelEdit = () => { 
    setEditingId(null); 
    setEditForm({ montant: "", date_encaissement: "", motif: "" }); 
  };

  const handleEditChange = (e) => setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const submitEdit = async (id) => {
    if (!editForm.montant || !editForm.date_encaissement) {
      setMessage({ type: "error", text: "Montant et date requis." });
      return;
    }

    try {
      setEditLoading(true);
      await axios.put(`${API_URL}/api/transfert/encaissement/${id}`, {
        montant: parseFloat(editForm.montant),
        date: editForm.date_encaissement,
        motif: editForm.motif,
      });

      setMessage({ type: "success", text: "✅ Encaissement modifié." });
      setEditingId(null);
      setEditForm({ montant: "", date_encaissement: "", motif: "" });
      await fetchMontantDisponible();
      await fetchEncaissements();
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Erreur lors de la modification." });
    } finally { 
      setEditLoading(false); 
    }
  };

  const formatDate = (d) => { 
    if (!d) return ""; 
    const dt = new Date(d); 
    return isNaN(dt) ? d : dt.toLocaleDateString(); 
  };

  return (
    <div className="transfert-page">

      {/* === TROIS POINTS === */}
      <div className="top-bar">
        <div className="menu-points" onClick={() => setMenuOpen(!menuOpen)}>
          ⋮
        </div>
      </div>

      {/* === OVERLAY FIXE PAR-DESSUS TOUT === */}
      {menuOpen && !showHistory && (
        <div className="overlay-card" onClick={() => setMenuOpen(false)}>
          <div className="overlay-card-inner">
            <button
              className="overlay-btn"
              onClick={() => {
                setShowHistory(true);
                setMenuOpen(false);
              }}
            >
              📜 Historique
            </button>
          </div>
        </div>
      )}

      {/* === VUE HISTORIQUE === */}
      {showHistory ? (
        <div className="history-fullscreen">
          <div className="history-header">
            <button className="btn-back" onClick={() => setShowHistory(false)}>⬅ Retour</button>
            <h3>📜 Historique des transferts</h3>
          </div>

          <div className="history-body">
            {encaisLoading ? <p>⏳ Chargement...</p> :
             encaisError ? <p className="error">{encaisError}</p> :
             encaissements.length === 0 ? <p>Aucun encaissement trouvé.</p> : (
              <div className="table-wrapper">
                <table className="encaissement-table">
                  <thead>
                    <tr>
                      <th>Montant</th>
                      <th>Date</th>
                      <th>Motif</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {encaissements.map(row => (
                      <tr key={row.id}>
                        <td>
                          {editingId === row.id ? (
                            <input type="number" name="montant" value={editForm.montant} onChange={handleEditChange} />
                          ) : Number(row.montant).toFixed(2)}
                        </td>

                        <td>
                          {editingId === row.id ? (
                            <input type="date" name="date_encaissement" value={editForm.date_encaissement} onChange={handleEditChange} />
                          ) : formatDate(row.date_encaissement || row.date)}
                        </td>

                        <td>
                          {editingId === row.id ? (
                            <input type="text" name="motif" value={editForm.motif} onChange={handleEditChange} />
                          ) : row.motif || "—"}
                        </td>

                        <td>
                          {editingId === row.id ? (
                            <>
                              <button className="btn small" onClick={() => submitEdit(row.id)} disabled={editLoading}>{editLoading ? "⏳" : "validé"}</button>
                              <button className="btn small" onClick={cancelEdit}>✖</button>
                            </>
                          ) : (
                            <>
                              <button className="btn small" onClick={() => startEdit(row)}>✎</button>
                              <button className="btn small danger" onClick={() => handleDelete(row.id)}>🗑</button>
                            </>
                          )}
                        </td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>

      ) : (
        /* === FORMULAIRE === */
        <div className="transfert-form-container">
          <h2>💸 Nouveau Transfert d’argent</h2>

          <div className="solde-box">
            <strong>Solde disponible :</strong>{" "}
            {montantDisponible !== null ? (
              <span className="solde-ok">{Number(montantDisponible).toFixed(2)} FCFA</span>
            ) : (<span className="solde-error">Indisponible</span>)}
          </div>

          <form onSubmit={handleSubmit} className="transfert-form">
            <div className="form-group">
              <label>Montant</label>
              <input type="number" name="montant" value={form.montant} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Date</label>
              <input type="date" name="date" value={form.date} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Motif</label>
              <textarea name="motif" value={form.motif} onChange={handleChange} />
            </div>

            {message && <div className={`message ${message.type}`}>{message.text}</div>}

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? "⏳ Enregistrement..." : "💾 Enregistrer le transfert"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
